import type { FC } from 'react';

import Pill from '@/common-components/Pill';
import { FUNNEL_STEPS } from '@/features/analytics/constants';
import type { OnboardingFunnelStats } from '@/types/stats.types';

interface OnboardingFunnelCardProps {
  funnel: OnboardingFunnelStats;
}

/**
 * Replaces the prototype's "Fraud Detection" panel, which had no data source.
 *
 * Each bar is drawn relative to the top of the funnel, so the narrowing is visible rather than
 * having to be inferred from the numbers.
 */
const OnboardingFunnelCard: FC<OnboardingFunnelCardProps> = ({ funnel }) => {
  const top = Math.max(1, funnel.verified);

  return (
    <section className="rounded-2xl border border-[var(--color-divider)] bg-white p-6 shadow-[var(--shadow-card)]">
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-[15px] font-extrabold text-[var(--color-ink)]">Onboarding Funnel</h2>
        <Pill tone="neutral">All time</Pill>
      </header>

      <div className="flex flex-col gap-3">
        {FUNNEL_STEPS.map((step) => {
          const value = funnel[step.key];
          const percent = Math.round((value / top) * 100);
          return (
            <div key={step.key}>
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-semibold text-[var(--color-ink)]">
                  {step.label}
                </span>
                <span className="shrink-0 text-[13px] font-extrabold text-[var(--color-ink)]">
                  {value.toLocaleString('en-IN')}
                  <span className="ml-1.5 text-[11px] font-semibold text-[var(--color-ink-muted)]">
                    {percent}%
                  </span>
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-[3px] bg-[var(--color-surface-muted-2)]">
                <div
                  className="h-full rounded-[3px] bg-[var(--color-brand-500)]"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <p className="mt-1 text-[11px] text-[var(--color-ink-muted)]">{step.hint}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default OnboardingFunnelCard;
